import styled from "@emotion/styled"
import {
	PButton,
	PInputSearch,
	PTable,
	PTableBody,
	PTableCell,
	PTableHead,
	PTableHeadCell,
	PTableHeadRow,
	PTableRow,
} from "@porsche-design-system/components-react"
import { gridGap, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { InteriorOptionView } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { Option } from "src/components"
import { ButtonGroup, Grid, GridItem, InteriorColorTile, Select, SmallIconOverlay } from "src/components"
import { selectCatalogInteriorMessages } from "./selectCatalogInterior.messages"

export interface SelectCatalogInteriorColorProps {
	interior: {
		options: InteriorOptionView[]
		setValue: (interiorOptionCode: string) => void
		value?: string
	}
	isSubmitting: boolean
	material: {
		options: Option[]
		setValue: (value: string | null) => void
		value: string | null
	}
	onSubmit: () => Promise<void>
	searchText: {
		setValue: (searchText: string) => void
		value: string
	}
	showCustomInteriorContainer: () => void
	tags: {
		options: Option[]
		setValue: (value: string | null) => void
		value: string | null
	}
}

export const SelectCatalogInteriorColor = ({
	interior,
	isSubmitting,
	material,
	onSubmit,
	searchText,
	showCustomInteriorContainer,
	tags,
}: SelectCatalogInteriorColorProps) => {
	const { formatMessage } = useIntl()

	return (
		<>
			<StyledGrid $rowGap={gridGap}>
				<GridItem size={{ base: 12, m: 4 }}>
					<Select
						label={formatMessage(selectCatalogInteriorMessages.tag)}
						name="interiorColorTag"
						options={tags.options}
						filter
						value={tags.value}
						onChange={tags.setValue}
					/>
				</GridItem>
				<GridItem size={{ base: 12, m: 4 }}>
					<Select
						label={formatMessage(selectCatalogInteriorMessages.material)}
						name="colorType"
						options={material.options}
						filter
						value={material.value}
						onChange={material.setValue}
					/>
				</GridItem>
				<GridItem size={{ base: 12, m: 4 }}>
					<PInputSearch
						name="searchText"
						value={searchText.value}
						clear
						indicator
						onInput={(event) => searchText.setValue((event.target as HTMLInputElement).value ?? "")}
					/>
				</GridItem>
			</StyledGrid>

			<PTable caption="Select Catalog Interior Color List">
				<PTableHead>
					<PTableHeadRow>
						<PTableHeadCell>
							<FormattedMessage {...selectCatalogInteriorMessages.name} />
						</PTableHeadCell>
						<PTableHeadCell>
							<FormattedMessage {...selectCatalogInteriorMessages.code} />
						</PTableHeadCell>
					</PTableHeadRow>
				</PTableHead>
				<PTableBody>
					{interior.options.map((interiorOption, index) => (
						<StyledPTableRow key={index} onClick={() => interior.setValue(interiorOption.optionCode)}>
							<PTableCell>
								<SmallIconOverlay
									icon="check"
									visible={interior.value === interiorOption.optionCode}
									backgroundHexCode={interiorOption.color.seatColorHexCode}
								>
									<InteriorColorTile
										seatColor={interiorOption.color.seatColorHexCode}
										seatStitchingColor={interiorOption.color.seatStitchingColorHexCode}
										cockpitColor={interiorOption.color.cockpitColorHexCode}
										cockpitStitchingColor={interiorOption.color.cockpitStitchingColorHexCode}
										label={interiorOption.name}
									/>
								</SmallIconOverlay>
							</PTableCell>
							<PTableCell>{interiorOption.optionCode}</PTableCell>
						</StyledPTableRow>
					))}
				</PTableBody>
			</PTable>

			<ButtonGroup>
				<PButton loading={isSubmitting} onClick={onSubmit}>
					<FormattedMessage {...formMessages.save} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={showCustomInteriorContainer}>
					<FormattedMessage {...selectCatalogInteriorMessages.customInterior} />
				</PButton>
			</ButtonGroup>
		</>
	)
}

const StyledPTableRow = styled(PTableRow)`
	cursor: pointer;
`

const StyledGrid = styled(Grid)`
	align-items: flex-end;
	margin-bottom: ${spacingStaticMd};
`
